/**
 * Date utility functions for campaign management using dayjs
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import advancedFormat from 'dayjs/plugin/advancedFormat';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);

/**
 * Campaign status based on end date
 */
export type CampaignStatus = 'ongoing' | 'ended';

/**
 * Calculate campaign status based on end date
 * @param endsDate - End date in YYYY-MM-DD format or 'YYYY-MM-DD HH:MM:SS UTC' format
 * @returns 'ongoing' if campaign is still active, 'ended' if past end date
 *
 * @example
 * ```typescript
 * const status1 = getCampaignStatus('2025-12-16');
 * const status2 = getCampaignStatus('2025-12-16 12:00:00 UTC');
 * console.log(status1, status2); // 'ongoing' or 'ended'
 * ```
 */
export function getCampaignStatus(endsDate: string): CampaignStatus {
  const endDate = parseEndDate(endsDate);
  const now = dayjs.utc();
  return now.isBefore(endDate) || now.isSame(endDate) ? 'ongoing' : 'ended';
}

/**
 * Parse end date string to dayjs object in UTC
 * Supports two formats:
 * - YYYY-MM-DD (treated as end of day UTC: 23:59:59.999)
 * - YYYY-MM-DD HH:MM:SS UTC (parsed as exact time)
 *
 * @param endsDate - End date string
 * @returns dayjs object in UTC
 */
function parseEndDate(endsDate: string): dayjs.Dayjs {
  // Check if the date matches the datetime format "YYYY-MM-DD HH:MM:SS" (optionally with " UTC")
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}( UTC)?$/.test(endsDate)) {
    // Format: "YYYY-MM-DD HH:MM:SS UTC"
    // Remove " UTC" suffix if present
    const dateTimeStr = endsDate.endsWith(' UTC')
      ? endsDate.slice(0, -4).trim()
      : endsDate.trim();
    return dayjs.utc(dateTimeStr, 'YYYY-MM-DD HH:mm:ss');
  } else {
    // Format: "YYYY-MM-DD" - treat as end of day UTC
    return dayjs.utc(endsDate, 'YYYY-MM-DD').endOf('day');
  }
}

/**
 * Calculate time remaining until campaign ends
 * @param endsDate - End date in YYYY-MM-DD format or 'YYYY-MM-DD HH:MM:SS UTC' format
 * @returns Human-readable time remaining (e.g., "3 months", "2 weeks", "5 days")
 *
 * @example
 * ```typescript
 * const timeLeft1 = getTimeRemaining('2025-12-16');
 * const timeLeft2 = getTimeRemaining('2025-12-16 12:00:00 UTC');
 * console.log(timeLeft1, timeLeft2); // "2 months" or "3 weeks" or "5 days"
 * ```
 */
export function getTimeRemaining(endsDate: string): string {
  const endDate = parseEndDate(endsDate);
  const now = dayjs.utc();

  // If campaign has ended
  if (now.isAfter(endDate)) {
    return '-';
  }

  const diffDays = endDate.diff(now, 'day');

  // Calculate months (approximate: 30 days = 1 month)
  if (diffDays >= 60) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? 's' : ''}`;
  }

  // Calculate weeks
  if (diffDays >= 14) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''}`;
  }

  // Calculate days
  if (diffDays >= 1) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
  }

  // Calculate hours for last day
  const diffHours = endDate.diff(now, 'hour');
  return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
}

/**
 * Format end date to a human-readable exact datetime in user's local timezone
 * @param endsDate - End date in YYYY-MM-DD format or 'YYYY-MM-DD HH:MM:SS UTC' format
 * @param includeTimezone - Whether to include timezone offset (default: false for backward compatibility)
 * @returns Formatted datetime string in user's local timezone
 *
 * @example
 * ```typescript
 * const formatted = formatExactDateTime('2025-10-16 12:00:00 UTC', true);
 * console.log(formatted); // "Oct 16, 1:00 PM (UTC+1)" (in user's local timezone with DST)
 *
 * const formattedNoTz = formatExactDateTime('2025-10-16 12:00:00 UTC');
 * console.log(formattedNoTz); // "Oct 16, 1:00 PM" (without timezone)
 * ```
 */
export function formatExactDateTime(endsDate: string, includeTimezone: boolean = false): string {
  const endDate = parseEndDate(endsDate);

  // Convert to JavaScript Date and let the browser handle timezone conversion
  const jsDate = endDate.toDate();

  // Format using dayjs in local mode
  const localDate = dayjs(jsDate);

  // Format the date and time - "Dec 23rd, 5PM"
  const day = localDate.date();
  const daySuffix = getDaySuffix(day);
  const formattedDateTime = localDate.format(`MMM D[${daySuffix}], hA`);

  // Add timezone offset if requested (e.g., "UTC-4", "UTC+1")
  if (includeTimezone) {
    // Get the timezone offset for the SPECIFIC DATE being displayed
    const offsetMinutes = jsDate.getTimezoneOffset();
    const offsetHours = Math.abs(offsetMinutes / 60);
    const offsetSign = offsetMinutes <= 0 ? '+' : '-'; // Inverted because getTimezoneOffset is opposite
    return `${formattedDateTime} (UTC${offsetSign}${offsetHours})`;
  }

  return formattedDateTime;
}

/**
 * Get the ordinal suffix for a day (st, nd, rd, th)
 * @param day - Day of month (1-31)
 * @returns Ordinal suffix string
 */
function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) {
    return 'th';
  }
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}
